import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import GalleryFooter from "containers/GalleryPage/Components/GalleryFooter";
import GalleryGate from "containers/GalleryPage/Components/GalleryGate";
import GallerySpinner from "containers/GalleryPage/Components/GallerySpinner";
import SingleView from "containers/GalleryPage/Components/SingleView";
import GalleryExpired, { isGalleryExpired } from "containers/GalleryPage/Components/GalleryExpired";
import Logo from "components/Logo";
import SEO from "components/SEO";
import { useGetSingleGif } from "./api/queries";
import { useGetChannelInfo } from "containers/GalleryPage/api/queries";
import { getUnlockToken } from "containers/GalleryPage/api/mutations";
import { getValidUrl } from "utils/helpers";

const MoveView = () => {
  const location = useLocation();
  // Hide Intercom widget on individual image pages
  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
    return () => {
      if (window.Intercom) {
        window.Intercom('update', { hide_default_launcher: false });
      }
    };
  }, []);
  const { slug, gifId } = useParams();

  // Check if URL uses numeric channel ID (used for redirect logic below)
  const isNumericId = /^\d+$/.test(slug);

  // rails opens a shared photo without a password (see useGetSingleGif). For a
  // rails build that still answers 401 `is_protected`, the request forwards the
  // share link's per-photo key (`?k=`) and any channel unlock the gallery gate
  // stored for this slug, and the gate below handles the 401. The token is read
  // on every render (as the gallery does) so the one stored by the gate is
  // picked up straight away; `onUnlocked` only has to force that render.
  const k = new URLSearchParams(location.search).get("k");
  const unlockToken = getUnlockToken(slug);
  const [, setUnlockCount] = useState(0);
  const {
    data: gifData,
    isLoading: gifLoading,
    isError: gifError,
    error: gifErrorBody,
  } = useGetSingleGif(gifId, { k, unlockToken });
  const isProtected = !!gifErrorBody?.is_protected;

  // Channel name, branding and canonical slug. This page renders one photo, so
  // it reads the gif-free channel endpoint rather than a page of the gallery.
  const { data: channel, isLoading: channelLoading } = useGetChannelInfo(slug);

  // The gif's own channel stub carries the colour; fall back to the channel.
  const gifChannel = gifData?.channel;
  const rawBackground = gifChannel?.gallery_color || channel?.gallery_color;
  const background = rawBackground
    ? rawBackground.includes("#") ? rawBackground : `#${rawBackground}`
    : "#000000";
  const currentUrl = `${window.location.origin}/ch/${slug}/${gifId}`;
  const logoImage = getValidUrl(channel?.gallery_banner) || getValidUrl(channel?.big_logo_image_url);

  // Channels are often named "<Event> Photo Booth" already; the SEO copy below
  // only appends the words when the name doesn't already carry them.
  const channelName = channel?.name;
  const nameSaysPhotoBooth = !!channelName && /photo\s*booth/i.test(channelName);

  // Redirect numeric IDs to canonical slug URL
  // IMPORTANT: Preserve query strings and hash for analytics tracking
  // This must come after all hooks but before the main render
  if (isNumericId && channel?.slug && channel.slug !== slug) {
    return (
      <Navigate
        to={`/ch/${channel.slug}/${gifId}${location.search}${
          location.hash || ""
        }`}
        replace
      />
    );
  }

  if (isProtected) {
    // The unlock is stored per slug, so a numeric-id URL waits for the channel
    // lookup (and the canonical-slug redirect above) before offering the gate.
    if (isNumericId && channelLoading) {
      return (
        <div className="bg-black h-screen w-screen">
          <GallerySpinner />
        </div>
      );
    }
    return (
      <GalleryGate
        channelId={slug}
        galleryColor={background}
        onUnlocked={() => setUnlockCount((n) => n + 1)}
      />
    );
  }

  if (isGalleryExpired(gifErrorBody)) {
    return <GalleryExpired />;
  }

  if (gifError) {
    return (
      <div>
        <SEO
          title="Photo Booth Moment Not Found"
          description="The photo booth moment you're looking for could not be found."
          noIndex={true}
        />
        <div className="h-screen w-screen flex items-center justify-center">
          <h1>Something's not right here..</h1>
        </div>
        <GalleryFooter slug={slug} />
      </div>
    );
  }
  return gifLoading ? (
    <div className="bg-black h-screen w-screen">
      <GallerySpinner />
    </div>
  ) : (
    <div
      className="min-h-screen w-full"
      style={{ background: background }}
    >
      <SEO
        title={
          gifData?.title
            ? `${gifData.title}${channelName
                ? nameSaysPhotoBooth
                  ? ` - ${channelName}`
                  : ` - ${channelName} Photo Booth`
                : ""}`
            : channelName
              ? nameSaysPhotoBooth
                ? `Moment from ${channelName}`
                : `Photo Booth Moment from ${channelName}`
              : "Photo Booth Moment"
        }
        description={
          channelName
            ? nameSaysPhotoBooth
              ? `View and share this moment from ${channelName} - captured with Movebooth.`
              : `View and share this photo booth moment from ${channelName} - captured with Movebooth.`
            : "View and share this photo booth moment - captured with Movebooth."
        }
        canonical={currentUrl}
        ogType="article"
        ogImage={gifData?.image_urls?.original || gifData?.image_url}
      />

      {/* Sticky header with logo */}
      <div className="sticky top-0 w-full h-[64px] flex items-center justify-center z-20 bg-inherit">
        <Link to={`/ch/${slug}`} className="max-w-[184px] max-h-[40px]">
          {logoImage ? (
            <img src={logoImage} alt="logo" className="max-w-[184px] max-h-[40px] w-auto h-auto object-contain" />
          ) : !channelLoading ? (
            <Logo withText={true} fill="white" />
          ) : null}
        </Link>
      </div>

      <div className="sm:flex sm:items-start sm:justify-center w-full">
        <SingleView gif={gifData} backgroundColor={background} channelSlug={slug} shareKey={k} />
      </div>
      <GalleryFooter slug={slug} />
    </div>
  );
};

export default MoveView;
